// src/hooks/usePersistentTimer.js
import { useState, useEffect, useRef } from 'react';

// A helper function to get a value from localStorage
const getStorageValue = (key, defaultValue) => {
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return defaultValue;
    }
  }
  return defaultValue;
};

// A helper function to format seconds into MM:SS
const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.max(0, totalSeconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const usePersistentTimer = (durationInMinutes, storageKey) => {
  const totalDurationInSeconds = durationInMinutes * 60;
  
  const intervalRef = useRef(null);

  const [timerState, setTimerState] = useState({
    secondsRemaining: 0,
    isRunning: false
  });

  const { secondsRemaining, isRunning } = timerState;

  // --- THIS IS THE NEWLY CORRECTED INITIALIZATION LOGIC ---
  useEffect(() => {
    // If duration is 0 or invalid, don't do anything.
    if (totalDurationInSeconds <= 0) {
      setTimerState({ secondsRemaining: 0, isRunning: false });
      return;
    }

    const savedState = getStorageValue(storageKey, null);

    // Check if there is a VALID, UNEXPIRED timer in storage
    if (savedState && savedState.secondsRemaining > 0) {
      let newSecondsRemaining = savedState.secondsRemaining;
      let newIsRunning = savedState.isRunning;

      // Account for time passed while tab was closed if it was running
      if (savedState.isRunning) {
        const timeElapsedOffline = Math.floor(Date.now() / 1000) - savedState.timestamp;
        newSecondsRemaining = savedState.secondsRemaining - timeElapsedOffline;
      }

      if (newSecondsRemaining <= 0) {
        // Timer expired while offline
        setTimerState({ secondsRemaining: 0, isRunning: false });
      } else {
        // Load the valid, running/paused state
        setTimerState({ 
          secondsRemaining: newSecondsRemaining, 
          isRunning: newIsRunning
        });
      }

    } else {
      // No saved state, OR saved state is expired (secondsRemaining <= 0)
      // Start a fresh timer.
      setTimerState({
        secondsRemaining: totalDurationInSeconds,
        isRunning: true
      });
    }
  // This dependency array is correct. It runs when the duration prop arrives
  // or when the storage key changes.
  }, [totalDurationInSeconds, storageKey]);
  // --- END OF NEW LOGIC ---

  // Save to localStorage whenever state changes
  useEffect(() => {
    // Only save if the timer is valid (duration > 0)
    // This prevents the 0-duration initial state from being saved.
    if (totalDurationInSeconds <= 0) return; 

    const stateToSave = {
      secondsRemaining,
      isRunning,
      timestamp: Math.floor(Date.now() / 1000)
    };
    localStorage.setItem(storageKey, JSON.stringify(stateToSave));
  }, [secondsRemaining, isRunning, storageKey, totalDurationInSeconds]);

  // --- Interval Effect (This logic was and is correct) ---
  useEffect(() => {
    // Stop if not running or time is up
    if (!isRunning || secondsRemaining <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return; 
    }

    // Start the interval
    intervalRef.current = setInterval(() => {
      // Use functional update to ensure we have the latest state
      setTimerState(prevState => {
        if (prevState.secondsRemaining <= 1) {
          clearInterval(intervalRef.current);
          return { secondsRemaining: 0, isRunning: false };
        }
        return { 
          ...prevState, 
          secondsRemaining: prevState.secondsRemaining - 1 
        };
      });
    }, 1000);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, secondsRemaining]); // Re-run when running state or remaining time boundary changes.

  // Function to toggle pause/resume
  const togglePlayPause = () => {
    if (totalDurationInSeconds === 0) return; 
    if (secondsRemaining <= 0) return; // Can't pause/play a finished timer

    setTimerState(prevState => ({
      ...prevState,
      isRunning: !prevState.isRunning
    }));
  };

  return {
    displayTime: formatTime(secondsRemaining),
    percentage: totalDurationInSeconds > 0 
      ? (secondsRemaining / totalDurationInSeconds) * 100 
      : 0,
    isRunning: totalDurationInSeconds > 0 ? isRunning : false, 
    togglePlayPause,
  };
};